// src/hooks/usePermission.js
import { useContext } from "react";
import { PermissionMenuContext } from "../contextApi/permissionBasedMenuContext";

/**
 * Custom hook to check capabilities (permissions) on the frontend.
 * 
 * Usage options:
 * 1. Single check (returns a single boolean in an array for easy destructuring):
 *    const [canCreate] = usePermission("quantative-data.create");
 * 
 * 2. Multiple checks (returns an array of booleans):
 *    const [canCreate, canAssign] = usePermission(["quantative-data.create", "data-owner.assign"]);
 * 
 * 3. Helper functions & raw arrays (when called with no arguments):
 *    const { hasPermission, userCapabilities } = usePermission();
 * 
 * @param {string|string[]} [capabilitiesToCheck] Single capability string or array of capabilities.
 * @param {string} [legacyCodeToCheck] The legacy permission code.
 * @param {boolean} [isModuleSubscription] Set to true to enforce strict company-level subscription check even for Admin users.
 * @returns {boolean[] | {hasPermission: function, userCapabilities: string[],
 *   capabilitiesLoaded: boolean}} The array form (single string or array of
 *   capabilities passed) returns booleans only - capabilitiesLoaded (whether
 *   the permission context has finished loading) is available exclusively on
 *   the no-arg object form. A caller that needs both a specific capability
 *   check and the loaded state must call usePermission() with no arguments
 *   and use the returned hasPermission helper instead.
 */
export function usePermission(capabilitiesToCheck, legacyCodeToCheck, isModuleSubscription = false) {
  const context = useContext(PermissionMenuContext);

  if (!context) {
    throw new Error("usePermission must be used within a PermissionBasedMenuProvider");
  }

  const { userCapabilities = [], userPermissionList = [], capabilitiesLoaded = false } = context;

  const isAdmin = userCapabilities.includes("*");

  const checkSingle = (capability, legacyCode, isSubCheck = false) => {
    if (!capability) return false;

    // 1. Subscription Gate: If this is a strict module subscription check, user (including Admin) must have company subscription for it
    if (isSubCheck && legacyCode && !userPermissionList.includes(legacyCode)) {
      return false;
    }

    // 2. Wildcard super-user bypass
    if (isAdmin) return true;

    // 3. Capability check
    return userCapabilities.includes(capability);
  };

  // If no arguments passed, return capabilities and helper
  if (capabilitiesToCheck === undefined) {
    return {
      hasPermission: checkSingle,
      userCapabilities,
      capabilitiesLoaded,
      isAdmin,
    };
  }

  // If array is passed, check each and return array of booleans
  if (Array.isArray(capabilitiesToCheck)) {
    return capabilitiesToCheck.map(cap => checkSingle(cap));
  }

  // If single string is passed, return [boolean] to support: const [canCreate] = usePermission("...")
  return [checkSingle(capabilitiesToCheck, legacyCodeToCheck, isModuleSubscription)];
}
